/*
  Back/Forward Cache is the feature from browsers that save the
  last/next page the user visited to avoid reloading when they
  navigate their browsing history.

  Sometimes though, the cache gets stale (like if they did some
  data change that should be reflected in the last page once they
  hit the back button).

  These utilities are meant to handle those scenarios by allowing
  actions to trigger expireBFCache(), signaling that the cache is
  now stale. Then pages that care about it can define their own
  rules of reloading.

  --- How to enable Back/Forward Browser Cache in dev for debugging ---

  It's quite hackish but it's the only way I found (besides running the entire
  app in production mode). We effectively disable the websocket connection
  that Vite uses to communicate with the dev server since websockets prevent
  the BF cache behavior.

  Step-by-step:

    > dev exec vite bash
    > apt-get install neovim
    > nvim node_modules/vite/dist/client/client.mjs

    - Look for the createConnection function definitions (there are 2)
    - Replace with something like "false" so it doesn't start a websocket connection

  Now you should be able to simulate the BF Cache in your browser :)
  You can test it in the dev tools "Application" tab, there should be a
  "Back/Forward Cache" item in the Background Services section.

  Remember to re-install the vite dependency afterwards so you restore
  the original behavior in your dev env.
*/

import { addEventListenerWithKey } from "shared/lib/utils"

// This is for pages that only want to trigger the reload when the cache was explicitly set as stale (by calling expireBFCache)
export const onExpiredBFCacheLoad = (key: String, reloadFunction: () => void) => {
  addEventListenerWithKey(window, "pageshow", key, (event: PageTransitionEvent) => {
    // event.persisted is true when the page loads from BFCache
    if (event.persisted && isBFCacheExpired()) {
      resetBFCacheFlag()
      reloadFunction()
    } else {
      resetBFCacheFlag()
    }
  })
}

// This is for pages that want to always reload on BFCache load, regardless of the expiration flag
export const onBFCacheLoad = (key: String, reloadFunction: () => void) => {
  addEventListenerWithKey(window, "pageshow", key, (event: PageTransitionEvent) => {
    // event.persisted is true when the page loads from BFCache
    if (event.persisted) reloadFunction()
  })
}

export const expireBFCache = () => sessionStorage.setItem("isBFCacheExpired", "true")
const resetBFCacheFlag = () => sessionStorage.removeItem("isBFCacheExpired")
const isBFCacheExpired = () => !!sessionStorage.getItem("isBFCacheExpired")
